import Select from 'app/frontend/base/components/Select'
import { useMemo } from 'react'
import { EventActivityLinkedPlanData } from '../hooks/useEventActivityLinkedPlanData'
import EventActivityPlan from './EventActivityPlan'
import EventActivitySummaryOptionContent from './EventActivitySummaryOptionContent'

type Props = {
  activities: wowaudit.EventActivity[]
  selectedActivityId?: number
  onSelectedActivityIdChange: (activityId: number) => void
  selectableParticipantCount: number
  activityIssuesById: Map<
    number,
    { selectedCount: number; tooManyPlayers: boolean }
  >
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
  isRaidPlansLoading: boolean
}

const defaultActivityHeaderIssues = {
  selectedCount: 0,
  tooManyPlayers: false,
}

const EventActivityList = ({
  activities,
  selectedActivityId,
  onSelectedActivityIdChange,
  selectableParticipantCount,
  activityIssuesById,
  linkedPlanDataByActivityId,
  isRaidPlansLoading,
}: Props) => {
  const selectedActivity = useMemo(
    () =>
      activities.find((activity) => activity.id === selectedActivityId) ??
      activities[0],
    [activities, selectedActivityId],
  )

  const options = useMemo(
    () =>
      activities.map((activity) => {
        const issues =
          activityIssuesById.get(activity.id) ?? defaultActivityHeaderIssues
        const linkedPlanData = linkedPlanDataByActivityId.get(activity.id)
        return {
          value: String(activity.id),
          sortLabel: '',
          label: activity.encounter?.name || activity.instance?.name || '-',
          customOptionFn: () => (
            <div className='w-full'>
              <EventActivitySummaryOptionContent
                activity={activity}
                issues={issues}
                hasLinkedPlanIssues={
                  linkedPlanData?.hasLinkedPlanIssues ?? false
                }
                linkedPlanIssueCount={linkedPlanData?.linkedPlanIssueCount ?? 0}
                metaPosition='right'
              />
            </div>
          ),
        }
      }),
    [activities, activityIssuesById, linkedPlanDataByActivityId],
  )

  if (!selectedActivity) return null

  const selectedActivityIssues =
    activityIssuesById.get(selectedActivity.id) ?? defaultActivityHeaderIssues
  const allSelectedForActivity =
    selectableParticipantCount > 0 &&
    selectedActivityIssues.selectedCount === selectableParticipantCount

  return (
    <div className='p-3'>
      <Select
        value={String(selectedActivity.id)}
        setValue={(nextValue) => {
          if (!nextValue) return
          const numericId = Number(nextValue)
          if (!Number.isFinite(numericId)) return
          onSelectedActivityIdChange(numericId)
        }}
        options={options}
        className='min-h-10'
      />

      <EventActivityPlan
        activity={selectedActivity}
        activities={activities}
        allSelectedForActivity={allSelectedForActivity}
        linkedPlanData={linkedPlanDataByActivityId.get(selectedActivity.id)}
        isLinkedPlanDataLoading={isRaidPlansLoading}
      />
    </div>
  )
}

export default EventActivityList
